import QRCode from 'qrcode';

import { QR_PROFILE_SOURCE } from '../constants/qrProfile';
import { routes } from '../routes/routes';
import { buildPublicProfileIdSegment } from './publicProfilePath';

export const PROFILE_QR_RENDER_OPTIONS = {
  width: 1024,
  margin: 2,
  errorCorrectionLevel: 'H' as const,
  color: {
    dark: '#000000',
    light: '#ffffff',
  },
};

export function buildProfilePath(
  userId: string | number,
  isMentor: boolean,
  name?: string,
  lastName?: string
): string {
  const idSegment = buildPublicProfileIdSegment(userId, name, lastName);
  const base = isMentor
    ? routes.MENTOR.replace(':id', idSegment)
    : routes.MENTEE.replace(':id', idSegment);
  return `${base}?source=${QR_PROFILE_SOURCE}`;
}

export function buildProfileQrUrl(
  userId: string | number,
  isMentor: boolean,
  name?: string,
  lastName?: string
): string {
  return `${window.location.origin}${buildProfilePath(userId, isMentor, name, lastName)}`;
}

async function profileQrDataUrl(profileUrl: string): Promise<string> {
  return QRCode.toDataURL(profileUrl, PROFILE_QR_RENDER_OPTIONS);
}

async function profileQrPngFile(profileUrl: string): Promise<File> {
  const dataUrl = await profileQrDataUrl(profileUrl);
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], 'profile-qr.png', { type: 'image/png' });
}

export async function downloadProfileQrPng(options: {
  userId: string | number;
  isMentor: boolean;
  fileLabel?: string;
  name?: string;
  lastName?: string;
}): Promise<void> {
  const url = buildProfileQrUrl(options.userId, options.isMentor, options.name, options.lastName);
  const dataUrl = await profileQrDataUrl(url);

  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = `werule-profile-qr-${options.fileLabel || options.userId}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function shareProfileQrLink(profileUrl: string): Promise<boolean> {
  try {
    const pngFile = await profileQrPngFile(profileUrl);
    if (navigator.canShare?.({ files: [pngFile] })) {
      await navigator.share({
        files: [pngFile],
        title: 'Profile QR',
        text: profileUrl,
      });
      return true;
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return false;
    }
  }

  if (navigator.share) {
    try {
      await navigator.share({ url: profileUrl, title: 'Profile link' });
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return false;
      }
    }
  }

  try {
    await navigator.clipboard.writeText(profileUrl);
    return true;
  } catch {
    return false;
  }
}
